import * as Sentry from '@sentry/react';
import { initializeApp } from 'firebase/app';
import type { Auth, User } from 'firebase/auth';
import {
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { firebaseConfig } from '@/config/firebase';
import { deleteFCMToken } from '@/services/fcm/fcmService';
import { removeFCMTokenGlobal } from './FCMProvider';

export { firebaseConfig };

const firebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(firebaseApp);

export const firebaseAuth = {
  GoogleAuthProvider,
  signInWithPopup: (provider: GoogleAuthProvider) => signInWithPopup(auth, provider),
  signInWithEmailAndPassword: (email: string, password: string) =>
    signInWithEmailAndPassword(auth, email, password),
  sendPasswordResetEmail: (email: string) => sendPasswordResetEmail(auth, email),
};

const HASURA_TOKEN_KEY = 'https://hasura.io/jwt/claims';

interface HasuraClaims {
  'x-hasura-allowed-roles': string[];
  'x-hasura-user-id': string;
}

interface FirebaseClaims {
  email?: string;
  name?: string;
  nickname?: string;
  sub?: string;
  [HASURA_TOKEN_KEY]?: HasuraClaims;
}

interface UserDataParams {
  email: string;
  name: string;
  nickname: string;
  sub: string;
  userId: string;
  roles: string[];
}

export interface AuthState {
  status: 'loading' | 'in' | 'out' | 'unauthorized';
  token?: string;
  userDataParams?: UserDataParams;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export const AuthFirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({ status: 'loading' });

  const createUnauthorizedUserData = useCallback((claims: FirebaseClaims): UserDataParams => {
    return {
      email: claims.email || '',
      name: claims.name || '',
      nickname: claims.nickname || claims.name || claims.email || '',
      sub: claims.sub || '',
      userId: claims.sub || '',
      roles: [],
    };
  }, []);

  const createUserDataFromHasuraClaims = useCallback(
    (claims: FirebaseClaims, hasuraClaim: HasuraClaims): UserDataParams => {
      return {
        roles: hasuraClaim['x-hasura-allowed-roles'] || [],
        email: claims.email || '',
        name: claims.name || '',
        nickname: claims.nickname || '',
        sub: claims.sub || '',
        userId: hasuraClaim['x-hasura-user-id'],
      };
    },
    []
  );

  const setClaimsInState = useCallback(
    (claims: FirebaseClaims, token: string) => {
      const hasuraClaim = claims[HASURA_TOKEN_KEY];
      if (!hasuraClaim) {
        console.warn(
          "setClaimsInState called without Hasura claims. Setting auth status to 'unauthorized'."
        );
        setAuthState({
          status: 'unauthorized',
          token,
          userDataParams: createUnauthorizedUserData(claims),
        });
        return;
      }

      const userDataParams = createUserDataFromHasuraClaims(claims, hasuraClaim);
      setAuthState({ status: 'in', token, userDataParams });

      if (Sentry.isInitialized()) {
        Sentry.setUser({
          id: userDataParams.userId,
          email: userDataParams.email,
          username: userDataParams.nickname,
        });

        Sentry.setContext('firebase', {
          roles: userDataParams.roles.join(', '),
        });

        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'User authenticated successfully',
          level: 'info',
          data: {
            userId: userDataParams.userId,
            roles: userDataParams.roles,
          },
        });
      }
    },
    [createUnauthorizedUserData, createUserDataFromHasuraClaims]
  );

  const handleUserSignIn = useCallback(
    async (user: User) => {
      try {
        const idTokenResult = await user.getIdTokenResult();
        const token = idTokenResult.token;
        const initialHasuraClaim = idTokenResult.claims[HASURA_TOKEN_KEY];

        if (initialHasuraClaim) {
          setClaimsInState(idTokenResult.claims as FirebaseClaims, token);
          return { token, hasClaims: true };
        } else {
          console.log(
            'No Hasura claims found in initial token. Setting auth status to "unauthorized". User ID:',
            user.uid
          );
          setAuthState({
            status: 'unauthorized',
            token: token,
            userDataParams: {
              email: user.email || '',
              name: user.displayName || '',
              nickname: user.displayName || user.email || '',
              sub: user.uid,
              userId: user.uid,
              roles: [],
            },
          });
          return { token, hasClaims: false };
        }
      } catch (error) {
        console.error('Error during Firebase auth state change processing:', error);
        setAuthState({
          status: 'unauthorized',
          token: undefined,
          userDataParams: {
            email: user.email || '',
            name: user.displayName || '',
            nickname: user.displayName || user.email || '',
            sub: user.uid,
            userId: user.uid,
            roles: [],
          },
        });
        return { token: undefined, hasClaims: false };
      }
    },
    [setClaimsInState]
  );

  const setupClaimsPolling = useCallback(
    (user: User) => {
      const intervalId = setInterval(async () => {
        try {
          const newIdTokenResult = await user.getIdTokenResult(true);
          const hasuraClaim = newIdTokenResult.claims[HASURA_TOKEN_KEY];

          if (hasuraClaim) {
            setClaimsInState(newIdTokenResult.claims as FirebaseClaims, newIdTokenResult.token);
          }
        } catch (error) {
          console.error('Error polling for claims:', error);
        }
      }, 60000);

      return () => clearInterval(intervalId);
    },
    [setClaimsInState]
  );

  useEffect(() => {
    let claimsPollingCleanup: (() => void) | undefined;

    try {
      const unsubscriber = onAuthStateChanged(auth, async (user) => {
        console.log('Firebase auth state changed:', user ? 'user logged in' : 'user logged out');

        if (claimsPollingCleanup) {
          claimsPollingCleanup();
          claimsPollingCleanup = undefined;
        }

        if (user) {
          const { hasClaims } = await handleUserSignIn(user);
          if (hasClaims) {
            claimsPollingCleanup = setupClaimsPolling(user);
          }
        } else {
          setAuthState({ status: 'out' });
        }
      });

      return () => {
        unsubscriber();
        if (claimsPollingCleanup) {
          claimsPollingCleanup();
        }
      };
    } catch (error) {
      console.error('Firebase auth initialization error:', error);
      setAuthState({ status: 'out' });
    }
  }, [handleUserSignIn, setupClaimsPolling]);

  return <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthFirebaseProvider');
  }
  return context;
};

export const signOut = async () => {
  try {
    if (Sentry.isInitialized()) {
      Sentry.setUser(null);
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'User logged out',
        level: 'info',
      });
    }

    await removeFCMTokenGlobal();
    await deleteFCMToken();
    await firebaseSignOut(auth);
  } catch (error) {
    console.log(error);

    if (Sentry.isInitialized() && error instanceof Error) {
      Sentry.captureException(error, {
        tags: {
          errorType: 'auth',
          action: 'logout',
        },
      });
    }
  }
};
